import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Lock scroll synchronously, BEFORE the rAF — a counter increment + one
    // setAttribute, zero layout reads. Keeping it out of the rAF preserves the
    // facets-toggle INP fix (a layout read inside the rAF previously regressed
    // it from 200ms to 964ms). overflow:hidden (html[scroll-lock], base.css)
    // freezes the scroll offset in place — no scrollY read, scrollTo, or
    // body{position:fixed} full-page reflow needed.
    scrollLockHelper.lock();

    requestAnimationFrame(() => {
      dialog.showModal();

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      // Already inside rAF: setTimeout(0) lands after this frame paints, so the
      // dialog appears before the DialogOpenEvent listener cascade (app
      // trackers, sticky-state reads) runs — feedback-first for INP.
      setTimeout(() => this.dispatchEvent(new DialogOpenEvent()), 0);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    scrollLockHelper.unlock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          scrollLockHelper.lock();
        } else {
          scrollLockHelper.unlock();
        }
      }
    }
  },
  { capture: true }
);

/**
 * Ref-counted scroll-lock — the SINGLE owner of `html[scroll-lock] { overflow: hidden }`
 * (base.css). Every overlay that locks background scroll (dialogs, quick-add, the
 * cart-aside controller, and the `<details scroll-lock>` menu drawer above) funnels
 * through this, so the lock releases only when the LAST open overlay closes — stacking
 * in any order is leak-proof. overflow:hidden freezes the scroll offset in place, which
 * replaces the old `body { position: fixed }` + scrollTo dance that forced a ~1.3s
 * full-page reflow on every overlay open/close (INP forced-reflow fix, 2026-06-30).
 */
const scrollLockHelper = {
  _count: 0,
  lock() {
    if (this._count++ === 0) {
      document.documentElement.setAttribute('scroll-lock', '');
    }
  },
  unlock() {
    if (this._count <= 0) return; // guard a stray double-unlock (e.g. ESC + backdrop click)
    if (--this._count === 0) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  },
};

if (typeof window !== 'undefined') window.scrollLockHelper = scrollLockHelper;
export { scrollLockHelper };
